import React from 'react';
import moment from 'moment';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useObserver } from 'mobx-react';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TableCell from '@material-ui/core/TableCell';
import DeleteIcon from '@material-ui/icons/Delete';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import MediaIcon from './Icon';
import Link from 'next/link';
import withStores from '../../stores/withStores';
import Table from '../Table';
import useWidth from '../../utils/useWidth';
import adaTheme from '../../theme';
import ConfirmationDialog from '../ConfirmationDialog';
import useI18n from '../../i18n/useI18n';
import CreationForm from './CreationForm';
import {Image} from './Image';

const useStyles = makeStyles(theme => ({
  type: {
    textTransform: 'capitalize',
  },
  actionCell: {
    textAlign: 'right',
  },
  title: {
    wordBreak: 'break-all',
  },
  link: {
    display: 'block',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  chipWrap: {
    display: 'flex',
    // justifyContent: 'center',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0, 0.5, 0.5, 0),
    },
  },
  date: {
    position: 'relative',
    whiteSpace: 'nowrap',
    // '&:hover': {
    //   '&:after': {
    //     ...theme.typography.caption,
    //     content: 'attr(data-time)',
    //     position: 'absolute',
    //     marginLeft: theme.spacing(0.5),
    //   },
    // },
  },
  imgContainer: {
    position: 'relative',
    width: 150,
    height: 150,
  }
}));

const useHeaderAttributes = width => {
  const i18n = useI18n();
  switch (width) {
    case 'xs':
    case 'sm':
      return [
        { key: 'class', label: i18n.t('media.attributes.class'), width: 80 },
        {
          key: 'title',
          label: i18n.t('media.attributes.title'),
          width: 'auto',
        },
        {
          key: 'updatedAt',
          label: i18n.t('media.attributes.updatedAt'),
          width: 190,
        },
        { key: 'state', label: i18n.t('media.attributes.state'), width: 100 },
      ];
    default:
      return [
        { key: 'class', label: i18n.t('media.attributes.class'), width: 80 },
        {
          key: 'title',
          label: i18n.t('media.attributes.title'),
          width: 'auto',
        },
        {
          key: 'updatedAt',
          label: i18n.t('media.attributes.updatedAt'),
          width: 190,
        },
        { key: 'owner', label: i18n.t('media.attributes.owner'), width: 190 },
        { key: 'state', label: i18n.t('media.attributes.state'), width: 100 },
      ];
  }
};

const TooltipContent = props => {
  const { item } = props;
  const classes = useStyles();
  switch (item.sourceType) {
    case 'application/pdf':
      return (
        <object
          scrolling="no"
          data={`/ada-assets/${item.uuid}`}
          width="100%"
          height="300px"
          type="application/pdf"
        />
      );
    case 'image/jpg':
    case 'image/jpeg':
    case 'image/png':
    case 'image/gif':
    case 'image/svg+xml':
      return (
        <div className={classes.imgContainer}>
          <Image
            sizes="150px"
            assetId={item.uuid}
            alt={item.altText}
            layout="fill"
            objectFit="contain"
            objectPosition="center"
          />
        </div>
      );
    default:
      return (
        <Typography className={classes.tooltip} variant="caption">
          {item.class}
        </Typography>
      );
  }
};

const MediaInteractionCell = props => {
  const { datum, onDeleteAction } = props;
  const classes = useStyles();
  return (
    <TableCell classes={{ root: classes.actionCell }}>
      <Box display="flex" justifyContent="flex-end">
        <Box color="text.disabled">
          <IconButton
            onClick={() => onDeleteAction(datum)}
            color="inherit"
            variant="contained"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    </TableCell>
  );
};

const SelectItemCell = props => {
  const { datum, onAction } = props;
  const classes = useStyles();
  const i18n = useI18n();
  return (
    <TableCell classes={{ root: classes.actionCell }}>
      <Button
        color="primary"
        variant="contained"
        onClick={() => {
          onAction(datum);
        }}
      >
        {i18n.t('media.SelectItemCell.label')}
      </Button>
    </TableCell>
  );
};

SelectItemCell.propTypes = {
  datum: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired,
};

const getActionCell = (onSelectItem, { handleSetMediaToDelete }) => {
  if (onSelectItem) {
    return props => <SelectItemCell {...props} onAction={onSelectItem} />;
  }
  return props => (
    <MediaInteractionCell {...props} onDeleteAction={handleSetMediaToDelete} />
  );
};

const getItemTitle = item => item.title || item.originalFilename || item.source;

const MediaTable = props => {
  const { mediaStore, appStore, userStore, onSelectItem, hasLinks } = props;
  const classes = useStyles();
  const width = useWidth();
  const headerAttributes = useHeaderAttributes(width);
  const [mediaToDelete, setMediaToDelete] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const i18n = useI18n();

  React.useEffect(() => {
    const loadMedia = async () => {
      setIsLoading(true);
      await mediaStore.loadAllMedia();
      await appStore.loadAllTags();
      await userStore.loadAllRoles();
      await userStore.loadAllUsers();
      setIsLoading(false);
    };
    loadMedia();
  }, []);

  const handleCloseDialog = () => {
    setMediaToDelete(null);
  };

  const handleConfirmDialog = async () => {
    await mediaStore.deleteMediaItem(mediaToDelete.uuid);
    handleCloseDialog();
  };

  const handleSetMediaToDelete = item => {
    setMediaToDelete(item);
  };

  const ActionCell = getActionCell(onSelectItem, {
    handleSetMediaToDelete,
  });

  return useObserver(() => (
    <MuiThemeProvider theme={adaTheme}>
      <Table
        toolbarAddons={
          <Box ml="auto">
            <CreationForm />
          </Box>
        }
        isLoading={isLoading}
        data={mediaStore.items}
        headerAttributes={headerAttributes}
        queryAttributes={[
          'title',
          'originalFilename',
          'source',
          (i, query) =>
            i.tags.some(tag => tag && tag.toLowerCase().includes(query)),
        ]}
        defaultOrderBy="updatedAt"
        renderCell={(item, key) => {
          const value = item[key];
          switch (key) {
            case 'title':
              if (hasLinks) {
                return (
                  <React.Fragment>
                    <Link
                      href="/admin/media/[uuid]"
                      as={`/admin/media/${item.uuid}`}
                      passHref
                    >
                      <Typography
                        gutterBottom
                        component="a"
                        variant="body1"
                        className={cx(classes.link, classes.title)}
                      >
                        {getItemTitle(item)}
                      </Typography>
                    </Link>
                    <Box className={classes.chipWrap}>
                      {item.tags.map(t => (
                        <Chip
                          key={t}
                          size="small"
                          label={t}
                          color="secondary"
                        />
                      ))}
                    </Box>
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment>
                    <Typography
                      gutterBottom
                      variant="body1"
                      className={classes.title}
                    >
                      {item.title || item.originalFilename || item.source}
                    </Typography>
                    <Box className={classes.chipWrap}>
                      {item.tags.map(t => (
                        <Chip
                          key={t}
                          size="small"
                          label={t}
                          color="secondary"
                        />
                      ))}
                    </Box>
                  </React.Fragment>
                );
              }
            case 'class':
              return (
                <React.Fragment>
                  <Tooltip
                    placement="right"
                    title={<TooltipContent item={item} />}
                  >
                    <IconButton>
                      <MediaIcon type={value} sourceType={item.sourceType} />
                    </IconButton>
                  </Tooltip>
                </React.Fragment>
              );
            case 'updatedAt':
            case 'createdAt':
              const date = moment(value);
              return (
                <Tooltip title={date.format('LTS')} placement="right">
                  <Typography
                    className={cx(classes.type, classes.date)}
                    variant="body2"
                    component="span"
                  >
                    {date.format('LL')}
                  </Typography>
                </Tooltip>
              );
            case 'owner':
              const user = userStore.getUser(value);
              return (
                <Typography variant="body2">{user && user.name}</Typography>
              );
            default:
              return <Typography variant="body2">{item[key]}</Typography>;
          }
        }}
        ActionCell={ActionCell}
        actionCellWidth={150}
      />
      <ConfirmationDialog
        open={!!mediaToDelete}
        onCancel={handleCloseDialog}
        onConfirm={handleConfirmDialog}
        title={i18n.t('media.delete.title')}
      >
        <Typography variant="body1">
          {i18n.t('media.delete.confirmation', {
            media: <b>{mediaToDelete && getItemTitle(mediaToDelete)}</b>,
          })}
        </Typography>
      </ConfirmationDialog>
    </MuiThemeProvider>
  ));
};

export default withStores('mediaStore', 'userStore', 'appStore')(MediaTable);
